import { useEffect, useMemo, useState } from 'react'
import { MovieCardView } from '@govu/shared'
import {
  fetchMovieDetails,
  type TmdbMovieDetails,
} from '../tmdb/tmdb'
import { getEffectiveTmdbApiKey, useUserSettings } from '../settings/useUserSettings'
import { useToast, useConfirm } from '../ui'
import type { WatchlistEntry, WatchlistMovie } from './useWatchlists'
import { useWriteWatchlists } from './useWriteWatchlists'

interface Props {
  watchlist: WatchlistEntry
  onBack: () => void
  onOpenDetail: (tmdbId: number, type: 'movie' | 'tv') => void
}

/**
 * Full watchlist detail view — 1:1 port of the desktop
 * `WatchlistDetailView`. Header carries a back button, the watchlist
 * name (editable inline), a delete action, and a stats bar
 * (items / total runtime / avg TMDB). The body is a responsive grid of
 * `MovieCardView`s pulled from the shared UI package so the chrome
 * matches the library tab exactly (gold year, gold user-rating badge,
 * blue TMDB-rating badge, etc.).
 *
 * Ordering: the desktop view has a sort-by dropdown but no drag
 * reorder, so we expose the same sort options here rather than trying
 * to persist custom positions (which the PWA write path doesn't yet
 * model — every new movie lands with position 0). Per-card remove via
 * a × button shows on hover / focus.
 */
export function WatchlistDetailView({ watchlist, onBack, onOpenDetail }: Props) {
  const { updateWatchlistMeta, deleteWatchlist, removeMovieFromWatchlist } =
    useWriteWatchlists()
  const { showSuccess, showError } = useToast()
  const confirm = useConfirm()
  const tmdbIndex = useEagerMovieIndex(
    useMemo(
      () =>
        watchlist.movies
          .map((m) => m.tmdbId)
          .filter((id): id is number => typeof id === 'number'),
      [watchlist.movies]
    )
  )

  const [isEditingName, setIsEditingName] = useState(false)
  const [draftName, setDraftName] = useState(watchlist.name)
  const [sortBy, setSortBy] = useState<'addedAt' | 'title' | 'year' | 'rating'>(
    'addedAt'
  )
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    setDraftName(watchlist.name)
    setIsEditingName(false)
  }, [watchlist.id, watchlist.name])

  const stats = useMemo(() => {
    let totalRuntime = 0
    let ratingSum = 0
    let ratingCount = 0
    for (const m of watchlist.movies) {
      if (m.tmdbId == null) continue
      const tmdb = tmdbIndex.get(m.tmdbId)
      if (!tmdb) continue
      if (tmdb.runtime) totalRuntime += tmdb.runtime
      if (tmdb.rating > 0) {
        ratingSum += tmdb.rating
        ratingCount++
      }
    }
    return {
      total: watchlist.movies.length,
      totalRuntime,
      avgRating: ratingCount > 0 ? ratingSum / ratingCount : 0,
    }
  }, [watchlist.movies, tmdbIndex])

  const sortedMovies = useMemo(() => {
    const copy = [...watchlist.movies]
    copy.sort((a, b) => {
      const dir = sortOrder === 'asc' ? 1 : -1
      const ta = a.tmdbId != null ? tmdbIndex.get(a.tmdbId) : undefined
      const tb = b.tmdbId != null ? tmdbIndex.get(b.tmdbId) : undefined
      switch (sortBy) {
        case 'title': {
          const titleA = (ta?.title ?? a.externalTitle ?? '').toLowerCase()
          const titleB = (tb?.title ?? b.externalTitle ?? '').toLowerCase()
          return dir * titleA.localeCompare(titleB)
        }
        case 'year': {
          const ya = ta?.year ?? a.externalYear ?? 0
          const yb = tb?.year ?? b.externalYear ?? 0
          return dir * (ya - yb)
        }
        case 'rating': {
          const ra = ta?.rating ?? 0
          const rb = tb?.rating ?? 0
          return dir * (ra - rb)
        }
        case 'addedAt':
        default: {
          // Fall back to position when addedAt is unavailable, so the
          // list keeps a deterministic order across re-renders.
          const da = Date.parse(a.addedAt ?? '') || a.position
          const db = Date.parse(b.addedAt ?? '') || b.position
          return dir * (da - db)
        }
      }
    })
    return copy
  }, [watchlist.movies, sortBy, sortOrder, tmdbIndex])

  const handleSaveName = async () => {
    const trimmed = draftName.trim()
    if (!trimmed || trimmed === watchlist.name) {
      setIsEditingName(false)
      setDraftName(watchlist.name)
      return
    }
    setBusy(true)
    try {
      await updateWatchlistMeta(watchlist.id, { name: trimmed })
      setIsEditingName(false)
      showSuccess('Renamed')
    } catch (err) {
      console.error('[WatchlistDetailView] rename failed', err)
      showError('Failed to rename watchlist')
    } finally {
      setBusy(false)
    }
  }

  const handleDelete = async () => {
    const ok = await confirm({
      title: 'Delete watchlist?',
      message: `"${watchlist.name}" and its ${watchlist.movies.length} item${
        watchlist.movies.length === 1 ? '' : 's'
      } will be removed. This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    })
    if (!ok) return
    setBusy(true)
    try {
      await deleteWatchlist(watchlist.id)
      showSuccess('Deleted')
      onBack()
    } catch (err) {
      console.error('[WatchlistDetailView] delete failed', err)
      showError('Failed to delete watchlist')
    } finally {
      setBusy(false)
    }
  }

  const handleRemove = async (movie: WatchlistMovie) => {
    if (movie.tmdbId == null) return
    const tmdb = tmdbIndex.get(movie.tmdbId)
    const label = tmdb?.title ?? movie.externalTitle ?? `TMDB #${movie.tmdbId}`
    const ok = await confirm({
      title: 'Remove from watchlist?',
      message: `Remove "${label}" from "${watchlist.name}"?`,
      confirmLabel: 'Remove',
      danger: true,
    })
    if (!ok) return
    setBusy(true)
    try {
      await removeMovieFromWatchlist(watchlist.id, movie.tmdbId)
      showSuccess(`Removed from ${watchlist.name}`)
    } catch (err) {
      console.error('[WatchlistDetailView] remove failed', err)
      showError('Failed to remove from watchlist')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <button
            type="button"
            onClick={onBack}
            aria-label="Back to watchlists"
            className="shrink-0 rounded-md bg-bg-secondary p-2 text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary"
          >
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {watchlist.color && (
            <span
              className="mt-1 inline-block h-5 w-5 shrink-0 rounded-full ring-2 ring-white/10"
              style={{ backgroundColor: watchlist.color }}
            />
          )}

          <div className="min-w-0 flex-1">
            {isEditingName ? (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveName()
                    if (e.key === 'Escape') {
                      setIsEditingName(false)
                      setDraftName(watchlist.name)
                    }
                  }}
                  autoFocus
                  disabled={busy}
                  className="min-w-[12rem] flex-1 rounded-md border border-border-primary bg-bg-secondary px-2 py-1 text-lg font-bold text-text-primary focus:border-accent-primary/50 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={handleSaveName}
                  disabled={busy}
                  className="rounded-md bg-accent-primary px-3 py-1 text-xs font-semibold text-black hover:bg-accent-hover disabled:opacity-50"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditingName(false)
                    setDraftName(watchlist.name)
                  }}
                  disabled={busy}
                  className="rounded-md bg-bg-secondary px-3 py-1 text-xs text-text-secondary hover:bg-bg-tertiary disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="truncate text-xl font-bold text-text-primary">
                  {watchlist.name}
                </h2>
                {watchlist.isDefault && (
                  <span className="rounded bg-bg-tertiary px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
                    Default
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setIsEditingName(true)}
                  className="rounded-md bg-bg-secondary px-2 py-0.5 text-[11px] text-text-secondary hover:bg-bg-tertiary hover:text-text-primary"
                >
                  Rename
                </button>
              </div>
            )}
            {watchlist.description && !isEditingName && (
              <p className="mt-1 text-sm text-text-secondary">{watchlist.description}</p>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={handleDelete}
          disabled={busy}
          className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-400 transition-colors hover:bg-red-500/20 disabled:opacity-50"
        >
          Delete
        </button>
      </header>

      {/* Stats bar — matches desktop's three-stat grid */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Items" value={String(stats.total)} />
        <StatCard
          label="Total runtime"
          value={stats.totalRuntime > 0 ? formatHours(stats.totalRuntime) : '—'}
        />
        <StatCard
          label="Avg TMDB"
          value={
            stats.avgRating > 0 ? (
              <span className="text-blue-300">★ {stats.avgRating.toFixed(1)}</span>
            ) : (
              '—'
            )
          }
        />
      </div>

      {/* Sort toolbar */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border-primary pb-3">
        <span className="text-xs text-text-tertiary">Sort by</span>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
          className="rounded-md border border-border-primary bg-bg-secondary px-2 py-1 text-xs text-text-primary focus:border-accent-primary/50 focus:outline-none"
        >
          <option value="addedAt">Date added</option>
          <option value="title">Title</option>
          <option value="year">Year</option>
          <option value="rating">TMDB rating</option>
        </select>
        <button
          type="button"
          onClick={() => setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))}
          className="rounded-md border border-border-primary bg-bg-secondary px-2 py-1 text-xs text-text-secondary hover:bg-bg-tertiary hover:text-text-primary"
          aria-label={`Sort order: ${sortOrder === 'asc' ? 'ascending' : 'descending'}`}
          title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
        >
          {sortOrder === 'asc' ? '↑ Asc' : '↓ Desc'}
        </button>
      </div>

      {/* Grid */}
      {sortedMovies.length === 0 ? (
        <div className="py-16 text-center text-sm text-text-tertiary">
          <p className="mb-1 text-base">Empty watchlist</p>
          <p>Open a movie or series and add it from its detail modal.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8">
          {sortedMovies.map((movie) => (
            <WatchlistDetailTile
              key={movie.movieId}
              movie={movie}
              tmdb={movie.tmdbId != null ? tmdbIndex.get(movie.tmdbId) : undefined}
              onOpenDetail={onOpenDetail}
              onRemove={() => handleRemove(movie)}
              disabled={busy}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function StatCard({
  label,
  value,
}: {
  label: string
  value: React.ReactNode
}) {
  return (
    <div className="rounded-lg border border-border-primary bg-bg-secondary/60 p-3">
      <div className="text-lg font-bold text-text-primary">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-text-tertiary">
        {label}
      </div>
    </div>
  )
}

function WatchlistDetailTile({
  movie,
  tmdb,
  onOpenDetail,
  onRemove,
  disabled,
}: {
  movie: WatchlistMovie
  tmdb: TmdbMovieDetails | undefined
  onOpenDetail: (tmdbId: number, type: 'movie' | 'tv') => void
  onRemove: () => void
  disabled: boolean
}) {
  const externalPoster = normalizePosterPath(movie.externalPosterPath)
  const title =
    tmdb?.title ?? movie.externalTitle ?? `TMDB #${movie.tmdbId ?? movie.movieId}`
  const year = tmdb?.year ?? movie.externalYear ?? null
  const poster = tmdb?.posterUrl ?? externalPoster
  const playable = movie.tmdbId != null

  return (
    <div className="relative">
      <MovieCardView
        title={title}
        year={year}
        poster={poster}
        tmdbRating={tmdb?.rating ?? 0}
        userRating={null}
        isVirtual={true}
        ownerNames={[]}
        loading={playable && !tmdb}
        onOpen={() => {
          if (playable) onOpenDetail(movie.tmdbId!, 'movie')
        }}
      />
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onRemove()
        }}
        disabled={disabled}
        aria-label={`Remove ${title} from watchlist`}
        title="Remove from watchlist"
        className="absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-red-600/90 text-white opacity-0 shadow-lg transition-opacity duration-200 hover:bg-red-700 focus:opacity-100 group-hover:opacity-100 disabled:opacity-50"
      >
        <svg
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
}

function useEagerMovieIndex(
  tmdbIds: number[]
): Map<number, TmdbMovieDetails> {
  const { settings } = useUserSettings()
  const [index, setIndex] = useState<Map<number, TmdbMovieDetails>>(
    () => new Map()
  )

  useEffect(() => {
    // Falls back to the bundled developer key when the user hasn't supplied one.
    const apiKey = getEffectiveTmdbApiKey(settings)
    if (!apiKey || tmdbIds.length === 0) return
    const language = settings?.tmdbPreferredLanguage || 'en'
    let cancelled = false

    const pending: Promise<void>[] = []
    for (const id of tmdbIds) {
      pending.push(
        fetchMovieDetails(id, apiKey, language).then((d) => {
          if (cancelled || !d) return
          setIndex((prev) => {
            if (prev.has(id)) return prev
            const next = new Map(prev)
            next.set(id, d)
            return next
          })
        })
      )
    }
    void Promise.all(pending)

    return () => {
      cancelled = true
    }
  }, [tmdbIds, settings?.tmdbApiKey, settings?.tmdbPreferredLanguage])

  return index
}

function formatHours(minutes: number): string {
  if (minutes <= 0) return ''
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

function normalizePosterPath(raw: string | null): string | null {
  if (!raw) return null
  if (/^https?:\/\//i.test(raw)) return raw
  return `https://image.tmdb.org/t/p/w500${raw.startsWith('/') ? raw : `/${raw}`}`
}
